/**
 * Money and minutes for the revenue screens.
 *
 * Amounts arrive as decimal strings and are only turned into numbers here,
 * for display. Nothing on the page adds money across currencies: a total
 * is always per currency, because a ringgit beside a rupee is a different
 * number (invariant 49).
 */

const SYMBOL: Record<string, string> = { INR: "₹", MYR: "RM " };

/** "4980597.50" -> "₹49.81L" in INR, the way the business reads it. */
export function money(amount: string | number, currency: string, exact = false): string {
  const n = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(n)) return "—";
  const locale = currency === "INR" ? "en-IN" : "en-MY";
  if (exact) {
    return `${SYMBOL[currency] ?? ""}${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n)}`;
  }
  if (currency === "INR") {
    const abs = Math.abs(n);
    if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
    if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
    if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
  }
  return `${SYMBOL[currency] ?? ""}${new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(n)}`;
}

/** Billable seconds as whole minutes with Indian grouping. */
export const minutes = (secs: number): string =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(secs / 60);

/** A rate per minute. */
export const rate = (r: string | null | undefined, currency: string): string =>
  r === null || r === undefined ? "—" : `${SYMBOL[currency] ?? ""}${Number(r).toFixed(2)}/min`;

/** "2026-09-01" -> "Sep 2026". */
export function monthLabel(iso: string, short = false): string {
  const [y, m] = iso.split("-").map(Number);
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return short ? names[m - 1] : `${names[m - 1]} ${y}`;
}

/** "2026-09-01" -> "2026-09", the form the API takes. */
export const monthParam = (iso: string): string => iso.slice(0, 7);

/** Voicemail's share of billable bot time, or a dash when there is none. */
export function vmShare(f: { voicemail_billable_secs: number; bot_billable_secs: number }): string {
  if (f.bot_billable_secs === 0) return "—";
  return `${((f.voicemail_billable_secs / f.bot_billable_secs) * 100).toFixed(1)}%`;
}

export const STATUS_LABEL = {
  frozen: "Frozen",
  live: "Live",
  pending: "Closing",
  not_computed: "Not computed",
} as const;

export const STATUS_HINT = {
  frozen: "A statement was stored for this month; these numbers no longer move",
  live: "This month so far, priced from the mirror as it stands",
  pending:
    "The month is over but not frozen yet: it freezes on the 3rd, once every day has been read",
  not_computed: "The mirror holds nothing for this month. Run a month computation to fill it",
} as const;

/** A tier ladder from a plan's tiers, in hours where they are whole hours. */
export function threshold(fromMinutes: number): string {
  if (fromMinutes === 0) return "from 0";
  return fromMinutes % 60 === 0
    ? `${new Intl.NumberFormat("en-IN").format(fromMinutes / 60)} h`
    : `${new Intl.NumberFormat("en-IN").format(fromMinutes)} min`;
}

/** The name a per-call rule goes by in a column header or a line. */
export function ruleName(key: string): string {
  return key === "default" ? "Other calls" : `≤${key}s`;
}

type PulseLike = {
  rules: { up_to_secs: number; pulse_secs: number; charge?: string }[];
  default_pulse_secs: number;
  default_charge?: string;
};

/** "≤10s per call (10s usage), ≤20s → 20s, else 30s pulse" */
export function describePulse(p: PulseLike): string {
  const rule = (upTo: string, pulse: number, charge?: string) =>
    charge === "per_call"
      ? `${upTo} per call (${pulse}s usage)`
      : `${upTo} → ${pulse === 1 ? "per second" : `${pulse}s`}`;
  const rules = p.rules.map((r) => rule(`≤${r.up_to_secs}s`, r.pulse_secs, r.charge));
  const rest =
    p.default_charge === "per_call"
      ? `every other call per call (${p.default_pulse_secs}s usage)`
      : p.default_pulse_secs === 1
        ? "per second"
        : `${p.default_pulse_secs}s pulse`;
  return rules.length ? `${rules.join(", ")}, else ${rest}` : rest;
}

/** "2026-10-17" -> "17 Oct". A calendar date: never converted to a zone. */
export function dayLabel(iso: string): string {
  const [, m, d] = iso.split("-").map(Number);
  return `${d} ${monthLabel(`2000-${String(m).padStart(2, "0")}-01`, true)}`;
}

/** A signed change in money: "−₹4.2L", "+₹82K". */
export function signedMoney(delta: string | number, currency: string): string {
  const n = Number(delta);
  return `${n < 0 ? "−" : "+"}${money(Math.abs(n), currency)}`;
}

/** "-0.4453" -> "−44.5%"; null (no base) -> "new". */
export function signedPct(p: string | null | undefined): string {
  if (p === null || p === undefined) return "new";
  const n = Number(p) * 100;
  return `${n < 0 ? "−" : "+"}${Math.abs(n).toFixed(1)}%`;
}
